import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import { isFinite, isNull } from 'lodash-es'
import CurrencyInput from 'react-currency-input-field'
import useProcessorPrice from 'components/QuestionnaireRequestAutomated/hooks/useProcessorPrice'
import { formatRailsTimestamp } from 'lib/utilities'
import AwaitingPaymentModal from '../../../modal/awaitingPayment'
import ResetPriceModal from '../../../modal/resetPriceModal'
import { selectCurrentStatus, selectWorkflowType } from '../../../slices/globalSlice'
import { open as openInterstitialAction } from '../../../slices/interstitialSlice'

const STATUS_FOR_SET_PRICE = ['intake', 'price set', 'recorrected price set']

const PriceListItem = ({ selected, expand, value, label, name, onSelect = () => {}, onChange = () => {} }) => {
  const inputRef = useRef()
  const [itemPrice, setItemPrice] = useState(value)

  useEffect(() => {
    if (expand) {
      setItemPrice(value)
    }
  }, [expand])

  const onInputChange = (val) => {
    setItemPrice(val)
    onChange(val)
  }

  return (
    <li className="dome-d-flex dome-flex-column dome-align-start dome-full-width">
      <div className="dome-d-flex dome-gap12 dome-align-baseline dome-full-width">
        <span onClick={onSelect} className="dome-cursor-pointer">
          <i className={clsx(' fa-circle', selected ? 'fa-solid' : 'fa-regular')}></i>
        </span>
        <span className="dome-p2 dome-color-dark-grey">{label}</span>
        {name && <span className="dome-p3 dome-color-med-grey">{name}</span>}
      </div>
      <div
        aria-expanded={expand}
        style={{ marginTop: expand ? 6 : 0 }}
        className="form-set-price-collapse dome-full-width"
      >
        <div
          className="dome-d-flex dome-flex-column auto-questionnaire-input-field"
        >
          <div className="input-field-container dome-d-flex dome-align-center dome-gap12">
            <CurrencyInput
              placeholder="$"
              value={itemPrice}
              allowDecimals={false}
              decimalScale={0}
              allowNegativeValue={false}
              prefix="$"
              step={1}
              onValueChange={onInputChange}
            />
          </div>
        </div>
      </div>
    </li>
  )
}

const PriceList = ({ expand, onSubmit }) => {
  const {
    currentPrice,
    presetPrice,
    possiblePrices,
    selected,
    price,
    onChange,
    onSubmit: setPrice,
    onSelect,
    revert,
    submitting,
    setSubmitting,
  } = useProcessorPrice()

  useEffect(() => {
    if (!expand && !submitting) {
      revert()
    }
  }, [expand, revert])

  const handleSubmit = async () => {
    setSubmitting(true)
    onSubmit()
    setPrice()
  }

  const disabled = !isFinite(parseFloat(price))

  return (
    <div aria-expanded={expand} className="price-section-form-set-price-container">
      <div className="price-section-form-set-price-body">
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <ul style={{ padding: 0, margin: 0 }} className="dome-d-flex dome-flex-column dome-gap6">
            {possiblePrices.map((item) => (
              <PriceListItem
                key={item.id}
                value={item?.dynamic_price && presetPrice?.id === item.id ? price : ''}
                name={item.name}
                selected={selected?.id === item.id}
                expand={item.dynamic_price && selected?.id === item.id}
                label={item.dynamic_price ? 'custom price' : `$${parseFloat(item.price)}`}
                onChange={onChange}
                onSelect={() => onSelect(item)}
              />
            ))}
          </ul>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled}
            className={clsx('dome-btn dome-btn-base ', disabled ? 'dome-btn-disabled' : 'dome-btn-go-green')}
          >
            {currentPrice ? 'edit price' : 'set price'}
          </button>
        </div>
      </div>
    </div>
  )
}

const Header = ({ priceSetExpanded, openPriceSet, closePriceSet }) => {
  const total = useSelector((state) => state.price.total)
  const currentStatus = useSelector(selectCurrentStatus)
  const canSetPrice = STATUS_FOR_SET_PRICE.includes(currentStatus)
  const paidAt = useSelector((state) => state.global.paidAt)

  const renderTitle = () => {
    switch (currentStatus) {
      case 'intake':
        return <span className="dome-p3 dome-color-lite-grey">pending</span>
      case 'price set':
      case 'recorrected price set':
        if (priceSetExpanded) return <span className="dome-p3 dome-color-lite-grey">editing price</span>
        return <span className="dome-p2 dome-color-dark-grey dome-text-w500">{total ? `$${parseFloat(total)}` : 'not set' }</span>
      default:
        return <span className="dome-p2 dome-color-dark-grey dome-text-w500">{total ? `$${parseFloat(total)}` : 'not set' }</span>
    }
  }

  const renderSubTitle = () => {
    if (currentStatus === 'intake' || priceSetExpanded) return null
    if (canSetPrice && !priceSetExpanded)
      return <span className="dome-p3 dome-color-med-grey">{total > 0 ? 'awaiting payment' : 'payment waived'}</span>
    if (paidAt)
      return (
        <span className="dome-p3 dome-color-med-grey">{`paid ${formatRailsTimestamp(paidAt, { dateOnly: true })?.toLowerCase()}`}</span>
      )

    return null
  }

  const renderBtnAction = () => {
    if (!canSetPrice)
      return (
        <button type="button" disabled={true} className="dome-btn dome-btn-base dome-btn-disabled">
          reset
        </button>
      )
    if (isNull(total)) return null

    if (priceSetExpanded)
      return (
        <div onClick={closePriceSet} className="dome-bg-med-grey" style={{ width: 30, height: 30, borderRadius: 6 }}>
          <div className="dome-d-flex dome-align-center dome-justify-center dome-full-width dome-full-height dome-cursor-pointer">
            <span>
              <i className="fa-regular fa-x fa-xs dome-color-white" />
            </span>
          </div>
        </div>
      )

    return (
      <button type="button" onClick={openPriceSet} className="dome-btn dome-btn-base dome-btn-aqua-stroke">
        reset
      </button>
    )
  }

  return (
    <div style={{ padding: 6, width: '100%' }}>
      <div className="dome-d-flex dome-gap18 dome-align-center">
        <div className="dome-flex-1">
          <div className="dome-d-flex dome-gap6">
            <span style={{ flexBasis: '17.5px' }}>
              <i className="fa-light fa-money-check-dollar"></i>
            </span>
            <div className="dome-d-flex dome-flex-column">
              <div>
                <span className="dome-p2 dome-color-dark-grey dome-text-w500">price: </span>
                {renderTitle()}
              </div>
              {renderSubTitle()}
            </div>
          </div>
        </div>
        {renderBtnAction()}
      </div>
    </div>
  )
}

export default function SetPriceTask() {
  const dispatch = useDispatch()
  const currentStatus = useSelector(selectCurrentStatus)
  const workflowType = useSelector(selectWorkflowType)

  const [priceSetExpanded, setPriceSetExpanded] = useState(false)
  const [openResetPriceModal, setOpenResetPriceModal] = useState(false)
  const [openAwaitingPaymentModal, setOpenAwaitingPaymentModal] = useState(false)

  const isStatusIntake = currentStatus === 'intake'
  const canSetPrice = STATUS_FOR_SET_PRICE.includes(currentStatus) && priceSetExpanded
  const openPriceSet = useCallback(() => setPriceSetExpanded(true), [])
  const closePriceSet = useCallback(() => setPriceSetExpanded(false), [])

  useEffect(() => {
    if (isStatusIntake) setPriceSetExpanded(true)
  }, [isStatusIntake])

  const handleSubmitPrice = () => {
    setPriceSetExpanded(false)
    isStatusIntake ? setOpenAwaitingPaymentModal(true) : setOpenResetPriceModal(true)
  }

  const openInterstitial = useCallback(() => {
    setOpenAwaitingPaymentModal(false)
    setOpenResetPriceModal(false)
    dispatch(openInterstitialAction())
  }, [])

  return (
    <div className="auto-questionnaire-processor-task-section">
      <div className="dome-d-flex dome-flex-column auto-questionnaire-processor-price-section">
        <Header priceSetExpanded={priceSetExpanded} openPriceSet={openPriceSet} closePriceSet={closePriceSet} />
        <PriceList expand={canSetPrice} onSubmit={handleSubmitPrice} />
        <AwaitingPaymentModal
          open={openAwaitingPaymentModal}
          onSubmit={openInterstitial}
          onClose={() => setOpenAwaitingPaymentModal(false)}
          workflowType={workflowType}
        />
        <ResetPriceModal
          open={openResetPriceModal}
          onSubmit={openInterstitial}
          onClose={() => setOpenResetPriceModal(false)}
          workflowType={workflowType}
        />
      </div>
    </div>
  )
}
